import React, { FC } from 'react'
import { Link } from 'gatsby'
import clsx from 'clsx'

/**
 * @interface FooterLinkItem
 * @description A single footer navigation link.
 * @property {string} label - Display text.
 * @property {string} to - Internal path or external URL.
 * @property {boolean} [external] - Whether the link opens an external site.
 */
interface FooterLinkItem {
	label: string
	to: string
	external?: boolean
}

/**
 * @interface FooterGroup
 * @description A titled group of footer links.
 * @property {string} heading - Section heading text.
 * @property {FooterLinkItem[]} links - Links within this section.
 */
interface FooterGroup {
	heading: string
	links: FooterLinkItem[]
}

const FOOTER_GROUPS: FooterGroup[] = [
	{
		heading: 'องค์กร',
		links: [{ label: 'อัปสวัสดิการ', to: '/' }]
	},
	{
		heading: 'บุคคลทั่วไป',
		links: [{ label: 'หมอ onLINE', to: '/b2c' }]
	},
	{
		heading: 'รู้จักเรามากกว่าเดิม',
		links: [
			{ label: 'บทความ', to: '/blog' },
			{ label: 'เกี่ยวกับเรา', to: '/about' },
			{ label: 'ติดต่อเรา', to: '/contact' },
			{ label: 'นโยบายความเป็นส่วนตัว', to: '/policy' }
		]
	},
	{
		heading: 'บริการอื่นๆ',
		links: [
			{ label: 'ดูแลผู้สูงอายุ', to: 'https://healthathome.in.th/', external: true },
			{ label: 'เนิร์สซิ่งโฮม', to: 'https://www.carecenter.healthathome.in.th/', external: true }
		]
	}
]

const linkClass = 'primary/small text-green-100 hover:text-white hover:underline font-normal'
const headingClass = 'primary/paragraph text-white font-medium'

/**
 * @component FooterLinkNode
 * @description Renders an internal Gatsby Link or an external anchor.
 */
const FooterLinkNode: FC<FooterLinkItem> = ({ label, to, external }) => {
	if (external) {
		return (
			<a
				href={ to }
				target="_blank"
				rel="noopener noreferrer"
				className={ linkClass }
			>
				{ label }
			</a>
		)
	}

	return (
		<Link
			to={ to }
			className={ linkClass }
		>
			{ label }
		</Link>
	)
}

/**
 * @component MenuGroup
 * @description A titled section containing a list of footer links.
 */
const MenuGroup: FC<FooterGroup> = ({ heading, links }) => (
	<div className="flex flex-col gap-4 shrink-0">
		<span className={ headingClass }>{ heading }</span>
		<div className="flex flex-col gap-2">
			{ links.map((link) => (
				<FooterLinkNode key={ link.label } { ...link } />
			)) }
		</div>
	</div>
)

/**
 * @component Menu
 * @description Footer navigation menu.
 * Desktop: three columns with 128px gap — first column groups "องค์กร" and "บุคคลทั่วไป" vertically.
 * Mobile: all groups stacked vertically with 40px gap.
 */
const Menu: FC = () => {
	return (
		<nav className={clsx(
			'flex flex-col gap-10 w-full',
			'sm:flex-row sm:gap-16',
			'md:flex-row md:gap-32'
		)}>
			<div className="flex flex-col gap-10">
				{ FOOTER_GROUPS.slice(0, 2).map((group) => (
					<MenuGroup key={ group.heading } { ...group } />
				)) }
			</div>

			{ FOOTER_GROUPS.slice(2).map((group) => (
				<MenuGroup key={ group.heading } { ...group } />
			)) }
		</nav>
	)
}

export default Menu
